import {
  Button,
  Card,
  Chip,
  DateColumn,
  Delimiter,
  FileManager,
  PageModal,
  Typography,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { useIssueTicket } from 'src/hooks/query/use-issue-ticket';
import { formatIssueTicketId } from '../utils/utils';
import { mapFileAssetsToForm } from 'src/components/common/resources/form/utils';
import { getFullName } from 'src/utils/user-data';
import { Issue } from '@otp/icons';
import { ImportanceField } from '../list/table/columns/importance';
import { StoresField } from 'src/components/common/table/columns/stores';
import { Comments } from 'src/components/common/comments';
import { DocumentNode, useMutation } from '@apollo/client';
import {
  CreateIssueCommentDocument,
  CreateIssueCommentDto,
  IssueTicketDocument,
} from 'src/graphql/generated/graphql';
import { useState } from 'react';
import { IssueTicketForm } from '../form';
import { StatusField } from '../list/table/columns/status';
import { TagsField } from '../list/table/columns/tags';

type IssueTicketDetailsProps = {
  issueTicketId: string;
  onClose: () => void;
  refetchQueries?: DocumentNode[];
  disabledFields?: {
    storeIds?: boolean;
  };
};

export const IssueTicketDetails = ({
  issueTicketId,
  onClose,
  refetchQueries = [],
  disabledFields,
}: IssueTicketDetailsProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'issue.ticket.details',
  });

  const { issueTicket } = useIssueTicket({ id: issueTicketId });
  const [createIssueComment, { loading }] = useMutation(
    CreateIssueCommentDocument,
  );
  const [displayEditForm, setDisplayEditForm] = useState(false);

  if (!issueTicket) {
    return null;
  }

  const formattedIssueTicketId = formatIssueTicketId(issueTicket);
  const title = formattedIssueTicketId + ' - ' + issueTicket.name;
  const files = mapFileAssetsToForm(issueTicket.fileAssets);

  const onNewComment = (data: Omit<CreateIssueCommentDto, 'issueTicketId'>) => {
    createIssueComment({
      variables: {
        data: {
          ...data,
          issueTicketId: issueTicket.id,
        },
      },
      refetchQueries: [IssueTicketDocument],
    });
  };

  return (
    <PageModal open={true} title={title} onClose={onClose}>
      <div className="w-full min-w-small md:min-w-medium">
        <Card className="w-full mb-4 bg-red-50 border-red-500">
          <div className="flex flex-row gap-2 justify-between">
            <Chip label={formattedIssueTicketId} />
            <StatusField status={issueTicket.status} />
          </div>
          <Typography
            className="underline ps-1"
            variant="subtitle1"
            color="red"
          >
            {issueTicket.name}
          </Typography>
          <Typography className="ps-1" variant="subtitle2">
            {t('by')} {getFullName(issueTicket.createdBy, ' ')}
          </Typography>
        </Card>
        <div>
          <div className="flex flex-row justify-between items-center gap-2">
            <Typography
              variant="subtitle1"
              className="mb-4 font-bold flex flex-row gap-2"
            >
              <Issue />
              {t('issue-details')}
            </Typography>
            <Button
              className="px-1 min-w-0"
              onClick={() => setDisplayEditForm(true)}
            >
              {t('edit')}
            </Button>
          </div>
          <div>
            <Typography variant="subtitle2" className="mb-2">
              {t('issue-description')}
            </Typography>
            <Typography variant="subtitle2">
              <div
                dangerouslySetInnerHTML={{ __html: issueTicket.description }}
              />
            </Typography>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('importance')}</Typography>
              <ImportanceField importance={issueTicket.importance} />
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('tags')}</Typography>
              <Typography variant="subtitle2" className="text-right">
                <TagsField tags={issueTicket.tags} />
              </Typography>
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('reported-on')}</Typography>
              <Typography variant="subtitle2" className="font-bold">
                <DateColumn date={issueTicket.createdAt} withTime={true} />
              </Typography>
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('reported-by')}</Typography>
              <Typography variant="subtitle2" className="font-bold">
                {getFullName(issueTicket.createdBy, ' ')}
              </Typography>
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('assigned-to')}</Typography>
              <Typography variant="subtitle2" className="font-bold">
                {issueTicket.assignedTo &&
                  getFullName(issueTicket.assignedTo, ' ')}
              </Typography>
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('stores')}</Typography>
              <Typography variant="subtitle2" className="text-right">
                <StoresField stores={issueTicket.stores} />
              </Typography>
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('last-updated')}</Typography>
              <Typography variant="subtitle2" className="font-bold">
                <DateColumn date={issueTicket.updatedAt} withTime={true} />
              </Typography>
            </div>
            <Delimiter className="my-2" />
          </div>
        </div>
        <div>
          <Comments
            title={t('issue-comments')}
            comments={issueTicket.comments}
            onNewComment={onNewComment}
            loading={loading}
          />
        </div>
        {files.length > 0 && (
          <div>
            <Typography variant="subtitle1" className="mb-2 font-bold">
              {t('attachments')}
            </Typography>
            <FileManager readonly={true} files={files} />
          </div>
        )}
      </div>
      {displayEditForm && (
        <IssueTicketForm
          issueTicket={issueTicket}
          disabledFields={disabledFields}
          refetchQueries={[...refetchQueries, IssueTicketDocument]}
          onSave={() => setDisplayEditForm(false)}
          onCancel={() => setDisplayEditForm(false)}
        />
      )}
    </PageModal>
  );
};
